import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export interface SoftSkillScore {
  expectation: number;
  candidate_result: number;
  score_range: string;
  description: string;
  implication: string;
  impact_on_work: string;
}

export interface SoftSkill {
  name: string;
  score: SoftSkillScore;
}

export interface SoftSkillsData {
  score: number;
  soft_skills: SoftSkill[];
  version: number;
}

interface SoftSkillsResponse {
  message: string;
  data: SoftSkillsData;
}

/**
 * Hook for fetching soft skills data
 */
export const useSoftSkills = (reportId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['softSkills', reportId],
    queryFn: async () => {
      if (!reportId) {
        throw new Error('Report ID is required');
      }

      const response = await apiClient.get<SoftSkillsResponse>(
        `/reports/${reportId}/soft-skills/`
      );
      return response.data.data;
    },
    enabled: !!reportId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch soft skills';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    softSkills: data,
    isLoading,
    error,
    refetch,
  };
};
